import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import WebcamCapture from '../../../components/imageCapture/WebcamCapture';
import ImageCrop from '../../../components/imageCapture/ImageCrop';
import { SttDialog } from '../../../sporttia/all';
import { fullName } from '../../../lib/utils';
import { useInteractionsFiles } from '../../../lib/hooks';
import { PERMISSIONS } from '../../../lib/permissions';

const Steps = {
    CAPTURING: 0,
    CROPPING: 1,
    DONE: 2,
};

type MshipData = {
    id: number;
    name?: string;
    surname1?: string;
    surname2?: string;
};

type Props = {
    mship: MshipData;
    open: boolean;
    onClose: () => void;
    onUploaded?: (response: unknown) => void;
};

export default function SttWebcamCaptureDialog({
    mship,
    open,
    onClose,
    onUploaded,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const { uploadFile } = useInteractionsFiles();
    const [step, setStep] = useState<number>(Steps.CAPTURING);
    const [imgSrc, setImgSrc] = useState<string | null>(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            setStep(Steps.CAPTURING);
        }
    }, [open]);

    useEffect(() => {
        if (imgSrc) {
            setStep(Steps.CROPPING);
        }
    }, [imgSrc]);

    useEffect(() => {
        if (croppedImageUrl) {
            setStep(Steps.DONE);
        }
    }, [croppedImageUrl]);

    function setCroppedImage(dataURL: string) {
        setCroppedImageUrl(dataURL);
    }

    function saveCroppedImage() {
        if (!croppedImageUrl) {
            console.log('No cropped image dataUrl');
            return;
        }

        if (!mship || !mship.id) {
            console.log('No membership to upload picture to');
            return;
        }

        setUploading(true);

        fetch(croppedImageUrl)
            .then((response: any) => response.blob())
            .then(
                (blob: any) =>
                    new File([blob], 'temp.jpg', { type: 'image/jpeg' }),
            )
            .then((file: any) => {
                uploadFile(
                    {
                        name: `${fullName(mship as any)}_${format(
                            new Date(),
                            'dd-MM-yyyy',
                        )}`,
                        type: 'USER_PHOTO',
                        idMship: mship.id,
                    } as any,
                    file,
                )
                    .then((r: any) => {
                        if (onUploaded && onUploaded.constructor === Function) {
                            onUploaded(r);
                        }
                    })
                    .catch((error: any) => {
                        console.log(error);
                    })
                    .finally(() => {
                        setUploading(false);
                    });
            });
    }

    // TODO: títulos para las diferentes secciones / instrucciones para el usuario

    let content: React.ReactElement | null = null;
    switch (step) {
        case Steps.CAPTURING:
            content = <WebcamCapture width={400} onCapture={setImgSrc} />;
            break;
        case Steps.CROPPING:
            content = (
                <ImageCrop
                    src={imgSrc as string}
                    cropWidth={277}
                    cropHeight={300}
                    targetWidth={277}
                    targetHeight={300}
                    dataReturnType="base64"
                    onCrop={(dataURL: any) => setCroppedImage(dataURL)}
                />
            );
            break;
        case Steps.DONE:
            content = croppedImageUrl ? (
                <Box
                    mt={3}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                >
                    <img src={croppedImageUrl} />
                </Box>
            ) : null;
            break;
    }

    return (
        <SttDialog
            maxWidth="sm"
            title={t('CaptureWithWebcam')}
            open={open}
            onClose={onClose}
            content={content}
            buttons={
                [
                    step > 0 && {
                        caption: t('app.goBack'),
                        icon: <ArrowBack />,
                        onClick: () => setStep(step - 1),
                    },
                    step < Steps.DONE
                        ? {
                              type: 'cancel',
                              onClick: onClose,
                          }
                        : {
                              type: 'save',
                              onClick: saveCroppedImage,
                              permission: PERMISSIONS.CUSTOMER.MODIFY,
                              loading: uploading,
                          },
                ] as any
            }
        />
    );
}
